import { useTranslation } from 'react-i18next';
import { Close, Download, Upload } from '@mui/icons-material';
import { translateBackend } from 'assets/i18n/config';
import { TranslatedString } from 'interfaces';
import { useEffect, useState } from 'react';
import { TabPanel } from '../../../../../components/tabs/ovTabs';
import {
  Button, Dialog, DialogContent, DialogFooter, DialogTitle, IconButton, PdfViewer, Tab, Tabs,
} from '../../../../2-component';
import { Box, Typography } from '../../../../1-primative';
import { downloadHtmlAsPdf } from '../../../../../util/downloadHtmlAsPdf';

export interface DocumentDialogOptions {
  enableRegeneratePdf?: boolean;
  allowToUploadAgreements?: boolean;
}

interface Props {
  handleClose: () => void,
  open: boolean,
  content: string,
  pdfUrl?: string,
  documentId?: string
  translatedDisplayName?: TranslatedString,
  options?: DocumentDialogOptions
  downloadFile?: (id: string) => void,
  regeneratePdf?: (id: string) => void,
  uploadNewVersion?: (id: string) => void,
}

export const DocumentDialog = ({
  open, handleClose, content, pdfUrl, documentId, translatedDisplayName, options = {}, downloadFile, regeneratePdf, uploadNewVersion,
}: Props): JSX.Element => {
  const { t } = useTranslation(['client']);
  const [tab, setTab] = useState<'pdf' | 'html'>(content ? 'html' : 'pdf');

  useEffect(() => {
    if (open) setTab(content ? 'html' : 'pdf');
  }, [content, open]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth='lg'>
      {translatedDisplayName && (
        <DialogTitle>
          <Box display='flex' width='100%' justifyContent='space-between' alignItems='center'>
            <Box display='flex' alignItems='center'>
              {translateBackend(translatedDisplayName)}

              <Box ml={2}>
                <Tabs scrollButtons='auto' value={tab} sx={{ overflow: 'auto' }} onChange={(_e: any, newValue: any) => setTab(newValue)}>
                  {content && <Tab label={t('document:headerTabs.html')} value='html' />}
                  {pdfUrl && <Tab label={t('document:headerTabs.pdf')} value='pdf' />}
                </Tabs>
              </Box>
            </Box>

            <IconButton onClick={handleClose}> <Close /> </IconButton>
          </Box>
        </DialogTitle>
      )}
      <DialogContent>
        <Box height='100%' overflow='auto'>
          <TabPanel value={tab} index="html"> <Typography id="modal-modal-description" dangerouslySetInnerHTML={{ __html: `${content}` }} /> </TabPanel>
          <TabPanel value={tab} index="pdf"> <PdfViewer pdfUrl={pdfUrl} fullWidth /> </TabPanel>
        </Box>

        {tab === 'pdf' && documentId && pdfUrl && (
          <Box display='flex' justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems='center' flexWrap='wrap' gap={1} marginTop={2}>
            {options.enableRegeneratePdf && regeneratePdf && (
              <Button variant='outlined' label={t('client:documents.regenerate')} onClick={() => regeneratePdf(documentId)} />
            )}
            {options.allowToUploadAgreements && uploadNewVersion && (
              <Button variant='tonal' leadingIcon={Upload} label={t('client:documents.uploadNewVersion')} onClick={() => uploadNewVersion(documentId)} />
            )}
            {downloadFile && (
              <Button variant='filled' leadingIcon={Download} label={t('client:documents.downloadHtmlAsPdf')} onClick={() => downloadFile(documentId)} />
            )}
          </Box>
        )}
      </DialogContent>

      {(tab === 'html' && content) && (
        <DialogFooter>
          <Box display='flex' justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems='center' flexWrap='wrap' gap={1} m={1}>
            {options.allowToUploadAgreements && uploadNewVersion && documentId && (
              <Button variant='tonal' leadingIcon={Upload} label={t('client:documents.uploadNewVersion')} onClick={() => uploadNewVersion(documentId)} />
            )}
            <Button variant='filled' leadingIcon={Download} label={t('client:documents.downloadHtmlAsPdf')} onClick={() => (
              downloadHtmlAsPdf(content, translateBackend(translatedDisplayName))
            )} />
          </Box>
        </DialogFooter>
      )}
    </Dialog>
  );
};
